import React, { useCallback, useState } from 'react';
import { Box, CardTypesEnum, ChevronDownIcon, ChevronUpIcon, costFormater, Inscription, LinkButton, NewTabIcon, RawButton } from '@common-fe/common-fe';

import routes from '@/common/routes';
import { BankingBalancePlanDetail } from '@/modules/employer/components/BankingBalance/types';
import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon';
import colors from '@/styles/colors';

import BankingBalancePlanAdjustments from './BankingBalancePlanAdjustments';
import BankingBalancePlanBasicInfo from './BankingBalancePlanBasicInfo';
import BankingBalancePlanChart from './BankingBalancePlanChart';
import BankingBalancePlanFunding from './BankingBalancePlanFunding';
import BankingBalancePlanOmnibus from './BankingBalancePlanOmnibus';

interface Props {
  plan: BankingBalancePlanDetail;
  orgId?: string;
  isFirst?: boolean;
}

const BankingBalancePlanCard: React.FC<Props> = ({ plan, orgId, isFirst }) => {
  const [isExpanded, setIsExpanded] = useState(plan?.isPlanLevelFunding !== false && isFirst);

  const planTitle = plan?.isPlanLevelFunding === false
    ? `${plan?.planName} (${plan?.planType})`
    : `${plan?.planName} (${plan?.planType}): ${costFormater(plan?.balance, true)}`;

  const handleSeePlanDetails = useCallback(() => {
    window.open(`${routes.PLAN_BY_ID(String(plan?.planId))}${location.search}`, '_blank');
  }, [plan?.planId]);

  return (
    <Box
      data-testid={`plan-card_${plan?.planId}`}
      background="canvas"
      round="container1Round"
      border={{ color: 'border2' }}
      pad={{ horizontal: 'spacing32', vertical: 'spacing24' }}
    >
      <RawButton
        onClick={plan?.isPlanLevelFunding !== false ? () => setIsExpanded(!isExpanded) : undefined}
        style={{ width: '100%', height: '40px', cursor: plan?.isPlanLevelFunding === false ? 'default' : 'pointer' }}
      >
        <Box direction="row" align="center" justify="between" width="100%">
          <Box direction="row" align="center" gap="spacing8">
            {plan?.isPlanLevelFunding !== false && (
              <Box width="24px" align="center" justify="center">
                {isExpanded ? (
                  <ChevronUpIcon data-testid={`plan-card_${plan?.planId}_chevron-up`} size="20px" color="iconPrimary" />
                ) : (
                  <ChevronDownIcon data-testid={`plan-card_${plan?.planId}_chevron-down`} size="20px" color="iconPrimary" />
                )}
              </Box>
            )}
            <Box
              direction="row"
              gap="spacing4"
              align="center"
              justify="center"
            >
              <PlanTypeIcon
                type={plan?.planType as CardTypesEnum}
                size="32px"
                color={colors?.cardTypes[plan?.planType as CardTypesEnum]}
              />
              <Inscription size="20px" lineHeight="28px" weight={400}>
                {planTitle}
              </Inscription>
            </Box>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <LinkButton
              onClick={handleSeePlanDetails}
              rightIcon={<NewTabIcon />}
            >
              See Plan Details
            </LinkButton>
          </Box>
        </Box>
      </RawButton>

      {isExpanded && plan?.isPlanLevelFunding !== false && (
        <Box direction="row" margin={{ top: 'spacing12' }}>
          {/* Left column: Details */}
          <Box flex gap="spacing4">
            <BankingBalancePlanBasicInfo plan={plan} />

            {plan?.balances?.fundingMethodSpecific && (
              <>
                <Box height="1px" background="border2" margin={{ vertical: 'spacing4' }} />
                <BankingBalancePlanFunding funding={plan?.balances?.fundingMethodSpecific} fundingMethod={plan?.fundingMethod} />
              </>
            )}

            {plan?.balances && (
              <>
                <Box height="1px" background="border2" margin={{ vertical: 'spacing4' }} />
                <BankingBalancePlanAdjustments balances={plan?.balances} />
              </>
            )}
          </Box>

          {/* Vertical divider */}
          <Box width="1px" background="border2" margin={{ horizontal: 'spacing24' }}/>

          {/* Right column: Chart + Omnibus */}
          <Box flex>
            <BankingBalancePlanChart planId={plan?.planId} accountId={plan?.accountId}/>
            <BankingBalancePlanOmnibus balance={plan?.balance} planId={plan?.planId} orgId={orgId} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BankingBalancePlanCard;
